import React, { useState } from 'react';
import Pick, { PickListHeader } from './Pick';
import SortButton from './SortButton';
import { formatExpectedReturn, formatDollarChange, toneOfSigned } from '../utils/returns';
import { formatEventDate, eventIdFor } from '../utils/builder';
import AutoPicksOption from './AutoPicksOption';
import { strategyLabel } from '../utils/strategyNames';

// Weights below this are rounding left over from the optimizer, not part of the mix
const MIN_WEIGHT = 0.0005;

// "YYYY-MM-DD" or an ISO time as a local calendar day (new Date("2026-11-07") is the 6th in the US)
function localDay(value) {
  if (!value) return null;
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return new Date(Number(text.slice(0, 4)), Number(text.slice(5, 7)) - 1, Number(text.slice(8, 10)));
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function eventDateLabel(value) {
  const day = localDay(value);
  if (!day) return null;
  return day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * The body of a custom (user-built) playbook's page, event first: the key numbers, the next
 * event with its picks, then the strategy mix and how the playbook is tracked. Its record
 * (accuracy, returns, charts) is shown above this by PlaybookView once the daily job tracks it.
 */
export default function CustomPlaybookDetails({ playbook, tracked = false, canEdit = false, onAutoPicksChange }) {
  const [sortField, setSortField] = useState('weight');
  const [sortDirection, setSortDirection] = useState('desc');

  const mix = (playbook.portfolio || [])
    .filter((item) => item.strategy && (item.weight || 0) > MIN_WEIGHT)
    .sort((a, b) => b.weight - a.weight);
  const topWeight = mix.length ? mix[0].weight : 1;
  // The daily job keeps picks for every upcoming card; this page shows the next event's
  const eventId = playbook.event_name ? eventIdFor(playbook.event_name) : null;
  const picks = (playbook.picks || []).filter((p) => !p.event || !eventId || p.event.id === eventId);
  const autoPicks = playbook.auto_picks !== false;
  const maxStake = Math.max(0, ...picks.map((p) => p.budget_amount || 0));

  const eventDay = localDay(playbook.event_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventPassed = eventDay ? eventDay < today : false;
  const updated = playbook.last_updated ? formatEventDate(playbook.last_updated) : null;

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedPicks = [...picks].sort((a, b) => {
    const key = { weight: 'budget_amount', odds: 'decimal_odds', strategies: 'supporting_strategies' }[sortField];
    const diff = (a[key] || 0) - (b[key] || 0);
    return sortDirection === 'asc' ? diff : -diff;
  });

  return (
    <div className="custom-playbook">
      <div className="results-summary custom-summary">
        <div className="summary-item">
          <h3>Expected yearly return</h3>
          <span className={`summary-value ${toneOfSigned(playbook.portfolio_returns)}`}>
            {formatExpectedReturn(playbook.portfolio_returns)}
          </span>
          <span className="summary-note">Backtest estimate when built</span>
        </div>
        <div className="summary-item">
          <h3>Last year on $5,000</h3>
          <span className={`summary-value ${toneOfSigned(playbook.previous_year_returns)}`}>
            {formatDollarChange(playbook.previous_year_returns)}
          </span>
          <span className="summary-note">Simulated over the last 44 events</span>
        </div>
        <div className="summary-item">
          <h3>Strategies</h3>
          <span className="summary-value">{mix.length}</span>
          <span className="summary-note">{picks.length} {picks.length === 1 ? 'pick' : 'picks'} on the next card</span>
        </div>
      </div>

      <section className="panel custom-event" aria-labelledby="custom-event-title">
        <div className="custom-event-head">
          <div>
            <span className="custom-kicker">{eventPassed ? 'Last event' : 'Next event'}</span>
            <h3 id="custom-event-title" className="custom-event-name">{playbook.event_name || 'No event yet'}</h3>
          </div>
          {eventDateLabel(playbook.event_date) && (
            <span className="tag">{eventDateLabel(playbook.event_date)}</span>
          )}
        </div>
        <p className="custom-event-note">
          {eventPassed
            ? 'This event has passed. Refresh picks to get this playbook\'s picks for the next card.'
            : tracked && autoPicks
              ? `Picked by Odikai's daily update on ${updated || 'its last run'}.`
              : `Picks as of ${updated || 'when it was built'}. Refresh picks to use the latest odds.`}
        </p>

        {sortedPicks.length === 0 ? (
          <p className="empty-note">None of this playbook's strategies pick a fight on this card.</p>
        ) : (
          <>
            <div className="sort-controls" role="group" aria-label="Sort picks">
              <span className="sort-label">Sort</span>
              <SortButton field="weight" label="Sort by Weight" currentField={sortField} direction={sortDirection} onClick={handleSort} />
              <SortButton field="odds" label="Sort by Odds" currentField={sortField} direction={sortDirection} onClick={handleSort} />
              <SortButton field="strategies" label="Sort by Support" currentField={sortField} direction={sortDirection} onClick={handleSort} />
            </div>
            <PickListHeader />
            <div className="picks-list">
              {sortedPicks.map((pick, index) => (
                <Pick
                  key={`${pick.pick_name}-${index}`}
                  pick_name={pick.pick_name}
                  opponent_name={pick.opponent_name}
                  type={pick.type}
                  weight={pick.budget_amount}
                  american_odds={pick.american_odds}
                  decimal_odds={pick.decimal_odds}
                  supporting_strategies={pick.supporting_strategies}
                  total_strategies={mix.length || 10}
                  maxWeight={maxStake}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {playbook.playbook_type === 'language' && playbook.description && (
        <section className="custom-section">
          <h3 className="custom-section-title">Your description</h3>
          <p className="custom-description">{playbook.description}</p>
        </section>
      )}

      <section className="custom-section" aria-labelledby="custom-mix-title">
        <h3 id="custom-mix-title" className="custom-section-title">Strategy mix</h3>
        {mix.length === 0 ? (
          <p className="empty-note">No strategy weights were saved with this playbook.</p>
        ) : (
          <ul className="mix-list">
            {mix.map((item) => {
              const { group, option } = strategyLabel(item.strategy);
              return (
                <li key={item.strategy} className="mix-row" title={item.strategy}>
                  <span className="mix-name">
                    <span className="mix-group">{group}</span>
                    {option && <span className="mix-option">{option}</span>}
                  </span>
                  <span className="mix-bar" aria-hidden="true">
                    <span className="mix-bar-fill" style={{ width: `${(item.weight / topWeight) * 100}%` }} />
                  </span>
                  <span className="mix-weight">{(item.weight * 100).toFixed(1)}%</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="custom-section" aria-labelledby="custom-tracking-title">
        <h3 id="custom-tracking-title" className="custom-section-title">Tracking</h3>
        <p className="custom-tracking-note">
          {tracked
            ? `Scored after every event since ${formatEventDate(playbook.tracking_started)}. Its record before then is a backtest: this playbook replayed over Odikai's recent events with today's models and betting rules.`
            : 'Not tracked yet. The next daily update gives it a backtested record and scores it after every event from then on.'}
        </p>
        {canEdit && onAutoPicksChange && (
          <AutoPicksOption checked={autoPicks} onChange={onAutoPicksChange} id="auto-picks-page" />
        )}
      </section>
    </div>
  );
}
